import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";

/* A dedicated key: the dashboard rewrites cc-transcription-dashboard-defaults
   wholesale after every import, which would wipe anything stored alongside it. */
const IMPORT_SETTINGS_KEY = "cc-ai-import-settings";
const EVENT_GROUP_KEY = "cc-event-group-defaults";

export const DEFAULT_IMPORT_MAX_CHARS = 32;
export const DEFAULT_IMPORT_MAX_LINES = 2;

/* The event group standards forms persist 9999 when a field is cleared. Treat that
   sentinel as "unset" here, otherwise a cleared field silently disables line
   breaking on every AI transcript import. */
const UNSET_SENTINEL = 9999;

function readJson(key) {
    try {
        return JSON.parse(localStorage.getItem(key)) || {};
    } catch (err) {
        console.error(err);
        return {};
    }
}

function sanitize(value, fallback) {
    const num = Number(value);

    if (!Number.isFinite(num) || num < 1 || num >= UNSET_SENTINEL) {
        return fallback;
    }

    return Math.round(num);
}

function escapeHtml(value) {
    return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function resolveEventGroupName(inputValue, fallback) {
    const trimmed = String(inputValue ?? "").trim();
    if (trimmed) {
        return trimmed;
    }

    const fallbackName = String(fallback ?? "").trim();
    return fallbackName || undefined;
}

/**
 * Resolves the Max Characters / Max Lines an AI transcript import will conform to.
 * @returns {{maxChars: number, maxLines: number, skipPrompt: boolean}}
 */
export function getImportSettings() {
    const importSettings = readJson(IMPORT_SETTINGS_KEY);

    if (importSettings.skipPrompt) {
        return {
            maxChars: sanitize(importSettings.maxChars, DEFAULT_IMPORT_MAX_CHARS),
            maxLines: sanitize(importSettings.maxLines, DEFAULT_IMPORT_MAX_LINES),
            skipPrompt: true,
        };
    }

    const eventGroupDefaults = readJson(EVENT_GROUP_KEY);

    return {
        maxChars: sanitize(eventGroupDefaults.maxChars, DEFAULT_IMPORT_MAX_CHARS),
        maxLines: sanitize(eventGroupDefaults.maxLines, DEFAULT_IMPORT_MAX_LINES),
        skipPrompt: false,
    };
}

/* cc-event-group-defaults is deliberately left alone so confirming an import never
   rewrites the user's global event group defaults. */
export function saveImportSettings({ maxChars, maxLines, skipPrompt }) {
    localStorage.setItem(
        IMPORT_SETTINGS_KEY,
        JSON.stringify({ maxChars, maxLines, skipPrompt }),
    );
}

/**
 * @param {object} messages - paraglide message functions
 * @param {object} [options]
 * @param {boolean} [options.force] - show the dialog even when the user opted out of it
 * @param {string} [options.defaultEventGroupName] - when set, show an Event Group Name field
 * @returns {Promise<{maxChars: number, maxLines: number, eventGroupName?: string}|null>} null if cancelled
 */
export async function promptImportSettings(messages, { force = false, defaultEventGroupName } = {}) {
    const current = getImportSettings();
    const showEventGroupName = defaultEventGroupName !== undefined;

    if (current.skipPrompt && !force) {
        return {
            maxChars: current.maxChars,
            maxLines: current.maxLines,
            eventGroupName: resolveEventGroupName("", defaultEventGroupName),
        };
    }

    const eventGroupNameField = showEventGroupName
        ? `
                <div class="mb-3">
                    <label for="swal-import-event-group-name" class="form-label fw-bold">${messages.ai_import_settings_event_group_name()}</label>
                    <input type="text" id="swal-import-event-group-name" class="form-control" value="${escapeHtml(defaultEventGroupName)}">
                </div>`
        : "";

    const result = await Swal.fire({
        ...SWAL_BASE,
        customClass: SWAL_CLASSES.confirmCancel,
        titleText: messages.ai_import_settings_title(),
        html: `
            <div class="text-start">
                <p>${messages.ai_import_settings_text()}</p>
                ${eventGroupNameField}
                <div class="mb-3">
                    <label for="swal-import-max-lines" class="form-label fw-bold">${messages.max_lines_per_event()}</label>
                    <input type="number" id="swal-import-max-lines" class="form-control" min="1" step="1" value="${current.maxLines}">
                </div>
                <div class="mb-3">
                    <label for="swal-import-max-chars" class="form-label fw-bold">${messages.max_characters_per_line()}</label>
                    <input type="number" id="swal-import-max-chars" class="form-control" min="1" step="1" value="${current.maxChars}">
                </div>
                <div class="form-check form-switch">
                    <input class="form-check-input" type="checkbox" id="swal-import-skip" ${current.skipPrompt ? "checked" : ""}>
                    <label class="form-check-label" for="swal-import-skip">${messages.ai_import_settings_skip_label()}</label>
                </div>
            </div>
        `,
        showCancelButton: true,
        confirmButtonText: messages.ok(),
        cancelButtonText: messages.cancel(),
        allowOutsideClick: false,
        allowEscapeKey: false,
        focusConfirm: false,
        preConfirm: () => {
            const popup = Swal.getPopup();
            const maxLines = Number(popup.querySelector("#swal-import-max-lines").value);
            const maxChars = Number(popup.querySelector("#swal-import-max-chars").value);
            const skipPrompt = popup.querySelector("#swal-import-skip").checked;
            const nameInput = popup.querySelector("#swal-import-event-group-name");

            if (
                !Number.isInteger(maxLines) ||
                maxLines < 1 ||
                !Number.isInteger(maxChars) ||
                maxChars < 1
            ) {
                Swal.showValidationMessage(messages.ai_import_settings_validator());
                return false;
            }

            return {
                maxChars,
                maxLines,
                skipPrompt,
                eventGroupName: showEventGroupName
                    ? resolveEventGroupName(nameInput?.value, defaultEventGroupName)
                    : undefined,
            };
        },
    });

    if (!result.isConfirmed) {
        return null;
    }

    saveImportSettings(result.value);

    return {
        maxChars: result.value.maxChars,
        maxLines: result.value.maxLines,
        eventGroupName: result.value.eventGroupName,
    };
}
